import {Injectable} from '@angular/core';
import {SalespointsService as API} from '../../generated/sales-terminal-client/api/api';
import {
  Salespoint,
  SalespointPageableType,
  SalespointWithAlias,
  SalespointWithBlanks,
  SalespointWithBlanksDetail,
  TicketBlanksRequest
} from '../../generated/sales-terminal-client';
import {Observable, tap} from 'rxjs';
import {SnackService} from '../snack/snack.service';
import {PageableResult, ScrollLoadService, SortableSearch} from '../../components/scroll-load-component/scroll-load.component';
import {ScrollLoad} from '../../components/scroll-load-component/ScrollLoad';

export interface SalespointGet extends SortableSearch {
  type?: SalespointPageableType;
  generatedForLegal?: boolean;
  filters?: {
    name?: string;
    targetSalespointId?: number;
    targetLegalPersonId?: number;
    targetLegalPersonIdIsNull?: boolean;
  }
}

interface SalespointGetConfiguration {
  superCentreLevel?: boolean;
  hideUsed?: boolean;
  includeAliasSalespoints?: boolean;
}

@Injectable()
export class SalespointsService implements ScrollLoadService<SalespointWithAlias | SalespointWithBlanks>{
  constructor(private _api: API,
              private snackService: SnackService) {}

  public search(data: SalespointGet): Observable<PageableResult<SalespointWithAlias | SalespointWithBlanks>> {
    return this._api.salespointPageableGet(
      data.type || SalespointPageableType.Salespoint,
      data.filters?.name,
      data.filters?.targetSalespointId,
      data.filters?.targetLegalPersonId,
      data.filters?.targetLegalPersonIdIsNull,
      data.generatedForLegal || null,
      data.page,
      data.pageSize
    ).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    ) as Observable<PageableResult<SalespointWithAlias | SalespointWithBlanks>>;
  }
  public displayScrollLoadOption(option: SalespointWithAlias | SalespointWithBlanks): string {
    return '<b>' + option.name + '</b>' + (option.alias ? (' (' + option.alias.name + ')') : '');
  }
  public displayScrollLoadSelection(selection: SalespointWithAlias | SalespointWithBlanks): string | undefined {
    if (!selection) { return ''; }
    return selection.name + (selection.alias ? (' (' + selection.alias.name + ')') : '');
  }
  public getScrollLoad(pageSize?: number, idKey?: string): ScrollLoad<SalespointWithAlias | SalespointWithBlanks> {
    return new ScrollLoad<SalespointWithAlias | SalespointWithBlanks>(pageSize || 50, idKey || 'id');
  }

  public getSalesPoints(conf: SalespointGetConfiguration = {}): Observable<SalespointWithAlias[]> {
    return this._api.salespointsGet(conf.superCentreLevel, conf.hideUsed, conf.includeAliasSalespoints).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getSalespoint(id: number): Observable<SalespointWithBlanksDetail> {
    return this._api.salespointsBlankIdGet(id).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public updateSalespoint(id: number, data: TicketBlanksRequest): Observable<SalespointWithBlanksDetail> {
    return this._api.salespointsBlankIdPut(id, data).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public setPageableQuery(query: string): SalespointGet {
    return { filters: { name: query } }
  }

  public getCentres(): Observable<Salespoint[]> {
    return this._api.centresGet().pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
}
